import React, { useState, useEffect } from 'react';
import { 
  useStripe, 
  useElements, 
  PaymentElement,
  Elements
} from '@stripe/react-stripe-js';
import { useStripeContext } from '../../contexts/StripeProvider';
import { paymentService } from '../../services/paymentService';
import Button from '../ui/Button';
import Icon from '../AppIcon';

const StripePaymentFormInner = ({
  clientSecret,
  amount,
  currency = 'EUR',
  orderData,
  customerInfo,
  onSuccess,
  onError,
  confirmButtonText = 'Confirmer le paiement',
  className = ''
}) => {
  const stripe = useStripe();
  const elements = useElements();
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [paymentReady, setPaymentReady] = useState(false);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [successData, setSuccessData] = useState(null);

  useEffect(() => {
    setErrorMessage('');
    setPaymentReady(false);
  }, [clientSecret]);

  useEffect(() => {
    if (elements) {
      const paymentElement = elements?.getElement(PaymentElement);
      if (paymentElement) {
        paymentElement?.on('ready', () => {
          setPaymentReady(true);
        });
      }
    }
  }, [elements]);

  const handleSubmit = async (event) => {
    event?.preventDefault();

    if (!stripe || !elements || !clientSecret) {
      setErrorMessage('Système de paiement non prêt. Veuillez réessayer.');
      return;
    }

    setIsProcessing(true);
    setErrorMessage('');

    try {
      const { orderId, orderNumber } = orderData || {};

      const { error: stripeError, paymentIntent } = await stripe?.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location?.origin}/service-booking-wizard`,
        },
        redirect: 'if_required'
      });

      if (stripeError) {
        setErrorMessage(stripeError?.message || 'Le paiement a échoué. Veuillez réessayer.');
        onError?.(stripeError);
      } else if (paymentIntent) {
        if (paymentIntent?.status === 'succeeded') {
          const { data, error } = await paymentService?.confirmPayment(paymentIntent?.id);
          
          const successResult = {
            paymentIntent,
            orderId,
            orderNumber,
            orderData: data,
            warning: error ? 'Paiement traité mais confirmation échouée. Veuillez contacter le support si nécessaire.' : undefined
          };

          setSuccessData(successResult);
          setShowSuccessAnimation(true);

          setTimeout(() => {
            setShowSuccessAnimation(false);
            onSuccess?.(successResult);
          }, 3000);
        } else if (paymentIntent?.status === 'requires_action') {
          setErrorMessage('Authentification supplémentaire requise. Veuillez compléter la vérification.');
        } else {
          setErrorMessage('Traitement du paiement incomplet. Veuillez réessayer.');
        }
      }
    } catch (error) {
      console.error('Erreur de traitement du paiement:', error);
      setErrorMessage('Une erreur inattendue s\'est produite. Veuillez réessayer.');
      onError?.(error);
    } finally {
      setIsProcessing(false);
    }
  };

  if (!stripe || !elements || !clientSecret) {
    return (
      <div className={`bg-card rounded-lg border border-border p-6 ${className}`}>
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-muted rounded w-1/4"></div>
          <div className="h-10 bg-muted rounded"></div>
          <div className="h-4 bg-muted rounded w-1/2"></div>
          <div className="h-10 bg-muted rounded"></div>
        </div>
        <p className="text-sm text-muted-foreground mt-4 text-center">
          Chargement du système de paiement...
        </p>
      </div>
    );
  }

  return (
    <div className={`relative bg-card rounded-lg border border-border p-6 ${className}`}>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-card-foreground">
              Détails de paiement
            </label>
            <PaymentElement 
              options={{
                fields: {
                  billingDetails: 'auto'
                },
                layout: 'tabs'
              }}
            />
          </div>
        </div>

        {errorMessage && (
          <div className="bg-error/10 border border-error/20 rounded-lg p-4">
            <p className="text-error text-sm">{errorMessage}</p>
          </div>
        )}

        <Button
          type="submit"
          disabled={!paymentReady || isProcessing || !stripe || !elements}
          className="w-full"
          size="lg"
          iconName={isProcessing ? "Loader2" : "CreditCard"}
          iconClassName={isProcessing ? "animate-spin" : ""}
          iconPosition="left"
        >
          {isProcessing ? 'Traitement du paiement...' : confirmButtonText}
        </Button>

        <div className="flex items-center justify-center space-x-2 text-xs text-muted-foreground">
          <Icon name="Shield" size={14} />
          <span>Sécurisé par Stripe • Vos informations de paiement sont cryptées</span>
        </div>
      </form>
      {showSuccessAnimation && (
        <div className="absolute inset-0 bg-white/95 backdrop-blur-sm rounded-lg flex items-center justify-center z-50">
          <div className="text-center space-y-6 p-8">
            <div className="relative">
              <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center animate-pulse">
                <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center animate-bounce">
                  <Icon name="Check" size={32} className="text-white" />
                </div>
              </div>
              <div className="absolute inset-0 w-20 h-20 mx-auto border-4 border-green-300 rounded-full animate-ping opacity-30"></div>
              <div className="absolute inset-0 w-20 h-20 mx-auto border-2 border-green-400 rounded-full animate-ping opacity-50" style={{ animationDelay: '0.5s' }}></div>
            </div>

            <div className="space-y-2">
              <h3 className="text-2xl font-bold text-green-600">
                Paiement réussi ! 🎉
              </h3>
              <p className="text-green-700 font-medium">
                Votre réservation a été confirmée
              </p>
              {successData?.orderNumber && (
                <p className="text-sm text-green-600">
                  Réservation #{successData?.orderNumber}
                </p>
              )}
            </div>

            <div className="bg-green-50 rounded-lg p-4">
              <p className="text-lg font-semibold text-green-800">
                {paymentService?.formatAmount(amount, currency)} Payé
              </p>
              <p className="text-xs text-green-600 mt-1">
                Traitement de votre réservation...
              </p>
            </div>

            <div className="flex justify-center space-x-1">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-bounce"></div>
              <div className="w-2 h-2 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
              <div className="w-2 h-2 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const StripePaymentForm = (props) => {
  const { stripePromise } = useStripeContext();

  if (!props?.clientSecret || !stripePromise) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <p className="text-center text-muted-foreground">
          Préparation du système de paiement...
        </p>
      </div>
    );
  }

  const elementsOptions = {
    clientSecret: props?.clientSecret,
    appearance: {
      theme: 'stripe'
    }
  };

  return (
    <Elements stripe={stripePromise} options={elementsOptions}>
      <StripePaymentFormInner {...props} />
    </Elements>
  );
};

export default StripePaymentForm;